import { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Form, Select } from 'antd';
import { Dayjs } from 'dayjs';
import styled from 'styled-components';

import { ButtonIw as ButtonIwBase } from '@/_pages/interwill/components/ButtonIw';
import { FieldContainerIw as FieldContainerIwBase } from '@/_pages/interwill/components/FieldContainerIw';
import { SelectAssetCategory, SelectAssetOwnershipType } from '@/entities/assets';
import { Input, media } from '@/shared/components';
import { Alert } from '@/shared/components/Alert';
import { CurrenciesSelect as CurrenciesSelectBase } from '@/shared/components/currencies-select';
import { SelectCountry } from '@/shared/components/forms/fields/SelectCountry';
import { COLORS } from '@/shared/constants/colors';
import { useCurrencies } from '@/shared/hooks/use-currencies';
import { PrivateDocumentDtoType } from '@/shared/lib/forms/documents/private';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { AssetCategory, AssetDto, OwnershipType } from '@/shared/lib/sj-orm/models/asset.dto';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import {
  BankDto,
  ContactType,
  isBankContact,
  isBrokerContact,
} from '@/shared/lib/sj-orm/models/contacts';
import {
  PrivateDocumentCategory,
  PrivateDocumentDto,
} from '@/shared/lib/sj-orm/models/document/private.document.dto';
import { DatePicker } from '@/shared/uikit/DatePicker';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { IWCreateAssetModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../containers/ModalContainerV1';
import { InterwillStyledForm } from '../CreateEntityModal/styled';

import { useAssetForm } from './useAssetForm';
import { IWFormItemType, QuestionType, useForms } from './useForms';

export type CreateAssetParams = Partial<AssetDto> & {
  value?: string;
  bankName?: string;
  bankCounty?: string;
  bankAddress?: string;
  broker?: string;
  expirationDate?: Dayjs | string;
  dateOfIncorporation?: Dayjs | string;
  receipDate?: Dayjs | string;
  receipt?: string;
  purchaseReceipt?: string;
  receiptDocType?: PrivateDocumentCategory;
  receiptDocName?: string;
  receiptDocCountry?: string;
  receiptDocNumber?: string;
  receiptDocDate?: string;
  // place of record
  placeOfRecord?: string;
  placeOfRecordFinancialInstitution?: string;
  placeOfRecordDocument?: string;
  placeOfRecordCryptowallet?: string;
  placeOfRecordCryptoHolder?: string;
  placeOfRecordCryptoAccount?: string;
  placeOfRecordCryptoStorage?: string;
  placeOfRecordCryptoWalletName?: string;
  placeOfRecordCryptoDeviceLocation?: string;
  placeOfRecordDocumentType?: PrivateDocumentCategory;
  placeOfRecordDocumentName?: string;
  placeOfRecordDocumentCountry?: string;
  placeOfRecordDocumentNumber?: string;
  placeOfRecordDocumentLocation?: string;
  placeOfRecordContactsType?: string;
  placeOfRecordContactsBankName?: string;
  placeOfRecordContactsBankCountry?: string;
  placeOfRecordContactsBankAddress?: string;
  placeOfRecordContactsBrokerName?: string;
  placeOfRecordContactsBrokerCountry?: string;
  placeOfRecordContactsBrokerAddress?: string;
  district?: string;
  province?: string;
  city?: string;
  street?: string;
  buildingNumber?: string;
  apartments?: string;
};

export const IWCreateAssetModal: FC<IWCreateAssetModalProps> = ({
  id,
  isEdit,
  title,
  category: editCategory,
  allowedCategories,
  onFinish,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'IWCreateAsset' });

  const [category, setCategory] = useState<AssetCategory | undefined>(editCategory);

  const forms = useForms();
  const modals = useModalsStore();

  const sjDatabase = useSJDatabase();
  const editAsset = sjDatabase.collections.assets?.findOne((item) => item.id === id);
  const { list } = useCurrencies();

  const sortedList = [...list].sort((a, b) => a.currencyCode.localeCompare(b.currencyCode));

  const [selectedCurrency, setSelectedCurrency] = useState(
    editAsset?.purchaseValue?.from || list[0]?.baseCurrency || 'USD',
  );

  const { handleCreateOrUpdateAsset } = useAssetForm({
    category,
    editAssetId: id,
    isEditMode: isEdit,
    selectedCurrency,
  });

  const getBank = (bankId: DtoId): BankDto | undefined => {
    const res = sjDatabase.collections.contacts?.findOne((item) => item.id === bankId);

    if (!res || (!isBankContact(res) && !isBrokerContact(res))) return undefined;

    if (res.type === ContactType.BROKER) {
      const bank: BankDto = {
        ...res,
        type: ContactType.BANK,
        bankAddress: res.brokerAddress,
      };
      return bank;
    }
    return res;
  };

  const getReceipt = (
    receiptId: DtoId,
  ): (PrivateDocumentDto & PrivateDocumentDtoType) | undefined => {
    return sjDatabase.collections.privateDocuments?.findById(receiptId);
  };

  const flattenAddressFields = (asset: AssetDto | undefined): CreateAssetParams => {
    if (!asset?.address) return asset || {};

    return {
      ...asset,
      country: asset.address.country ?? asset.country,
      district: asset.address.district,
      province: asset.address.district,
      city: asset.address.city,
      street: asset.address.street,
      buildingNumber: asset.address.buildingNumber,
      apartments: asset.address.apartment,
    };
  };

  const {
    control,
    handleSubmit: formHandleSubmit,
    setValue,
    getValues,
    watch,
    reset,
  } = useForm<CreateAssetParams>({
    defaultValues: editAsset
      ? {
          ...flattenAddressFields(editAsset),
          value: String(editAsset.purchaseValue?.value),
        }
      : {},
  });

  const [bank, holder, broker, receipt, purchaseReceipt, placeOfRecord] = watch([
    'bank',
    'holder',
    'broker',
    'receipt',
    'purchaseReceipt',
    'placeOfRecord',
  ]);

  const getBankData = () => {
    const bankId = bank || holder || broker;
    if (!bankId) return;

    if (bankId === 'new') {
      setValue('bankName', '');
      setValue('bankCounty', undefined);
      setValue('bankAddress', '');
    }
    const data = getBank(bankId);
    if (!data) return;
    setValue('bankName', data?.nickname);
    setValue('bankCounty', data?.selectedCountry);
    setValue('bankAddress', data?.bankAddress);
  };

  const getReceiptData = () => {
    const receiptId = receipt ?? purchaseReceipt;
    if (!receiptId) return;
    if (receiptId === 'new') {
      setValue('receiptDocType', undefined);
      setValue('receiptDocName', '');
      setValue('receiptDocCountry', undefined);
      setValue('receiptDocNumber', '');
      setValue('receiptDocDate', '');
    }

    const data = getReceipt(receiptId);
    if (!data) return;
    setValue('receiptDocType', data.privateCategory);
    setValue('receiptDocName', data.name);
    setValue('receiptDocCountry', data.country);
    setValue('receiptDocNumber', data.number);
    setValue('receiptDocDate', String(data.startDate) ?? '');
  };

  useEffect(() => {
    if (receipt || purchaseReceipt) {
      getReceiptData();
      return;
    }
    getBankData();
  }, [editAsset, bank, holder, broker, receipt, purchaseReceipt]);

  useEffect(() => {
    setValue('placeOfRecordCryptowallet', undefined);
    setValue('placeOfRecordCryptoHolder', undefined);
    setValue('placeOfRecordCryptoAccount', undefined);
    setValue('placeOfRecordCryptoStorage', undefined);
    setValue('placeOfRecordCryptoWalletName', undefined);
    setValue('placeOfRecordCryptoDeviceLocation', undefined);
    setValue('placeOfRecordDocumentType', undefined);
    setValue('placeOfRecordDocumentName', undefined);
    setValue('placeOfRecordDocumentCountry', undefined);
    setValue('placeOfRecordDocumentNumber', undefined);
    setValue('placeOfRecordDocumentLocation', undefined);
    setValue('placeOfRecordContactsType', undefined);
    setValue('placeOfRecordContactsBankName', undefined);
    setValue('placeOfRecordContactsBankCountry', undefined);
    setValue('placeOfRecordContactsBankAddress', undefined);
    setValue('placeOfRecordContactsBrokerName', undefined);
    setValue('placeOfRecordContactsBrokerCountry', undefined);
    setValue('placeOfRecordContactsBrokerAddress', undefined);
  }, [placeOfRecord]);

  const onFinishHandler = async (values: CreateAssetParams) => {
    const res = await handleCreateOrUpdateAsset(values);
    onFinish?.(res);
    modals.closeModal();
  };

  const isFiledDisplayed = (value: IWFormItemType): boolean => {
    if (!value.showWhen || !category) return true;

    const watchedFieldValue = watch(value.showWhen.field as keyof CreateAssetParams);
    const parentField = forms[category][value.showWhen.field];

    if (watchedFieldValue && value.showWhen.not && value.showWhen.not === watchedFieldValue) {
      return false;
    }

    if (
      (value.showWhen.isFilled && !watchedFieldValue) ||
      (!value.showWhen.isFilled && watchedFieldValue !== value.showWhen.is)
    ) {
      return false;
    }

    if (value.showWhen.check) {
      return value.showWhen.check(getValues);
    }

    if (parentField.showWhen) {
      return isFiledDisplayed(parentField);
    }
    return true;
  };

  return (
    <ModalContainerV1 title={title || t('title')}>
      <InterwillStyledForm onFinish={formHandleSubmit(onFinishHandler)}>
        {!isEdit && (
          <>
            <FieldContainerIw label={t('category.label')} required>
              <SelectAssetCategory
                style={{
                  width: '100%',
                }}
                onChange={(v) => {
                  setCategory(v);
                  reset();
                }}
                options={allowedCategories?.map((value) => ({ value }))}
              />
            </FieldContainerIw>
            <Separator />
          </>
        )}

        <InnerForm>
          {category &&
            Object.entries(forms[category]).map(([key, value]) => {
              if (!isFiledDisplayed(value)) {
                return null;
              }

              if (value.type === QuestionType.SECTION_TITLE) {
                return <SectionTitle key={key}>{value.label}</SectionTitle>;
              }

              return (
                <Controller
                  key={key}
                  name={key as keyof CreateAssetParams}
                  control={control}
                  rules={{ required: value.required }}
                  render={({ field, fieldState }) => (
                    <FieldContainerIw
                      $halfWidth={value.halfWidth}
                      label={value.label}
                      required={value.required}
                    >
                      <Form.Item validateStatus={fieldState.error ? 'error' : ''}>
                        {value.type === QuestionType.COUNTRY && <SelectCountry {...field} />}

                        {value.type === QuestionType.OWNERSHIP && (
                          <>
                            <SelectAssetOwnershipType {...field} showSearch />
                            {field.value === OwnershipType.INDIRECT && (
                              <AlertStyled
                                color="yellow"
                                title={t('inderectWarningTitle')}
                                text={t('inderectWarning')}
                              />
                            )}
                          </>
                        )}

                        {value.type === QuestionType.DATE && (
                          <DatePicker
                            format="DD MMMM YYYY"
                            placeholder={value.placeholder}
                            style={{
                              width: '100%',
                              height: 40,
                            }}
                            {...field}
                            value={field.value as string}
                          />
                        )}

                        {value.type === QuestionType.SELECT && (
                          <Select
                            options={value.items?.map((n) => ({
                              ...n,
                              label: n.label,
                            }))}
                            placeholder={value.placeholder || ''}
                            showSearch
                            {...field}
                            onChange={(data) => {
                              const listItem = value.items!.find((item) => data === item.value);

                              listItem?.onSelect?.(setValue);

                              if (listItem?.openCreateForm) {
                                modals.openModal({
                                  type: 'IWCreateAssetModal',
                                  title: listItem?.openCreateForm.formTitle,
                                  allowedCategories: listItem?.openCreateForm.allowedCategories,
                                  onFinish: listItem?.openCreateForm.onSubmit
                                    ? (asset) => {
                                        listItem?.openCreateForm?.onSubmit?.(asset, setValue);
                                      }
                                    : undefined,
                                });
                              }
                              field.onChange(data);
                            }}
                          />
                        )}

                        {value.type === QuestionType.MULTIPLE_SELECT && (
                          <Select
                            mode="multiple"
                            options={value.items?.map((n) => ({
                              ...n,
                              label: n.label,
                            }))}
                            showSearch
                            placeholder={value.placeholder || ''}
                            {...field}
                          />
                        )}

                        {value.type === QuestionType.CURRENCY && (
                          <CurrencyWrapper>
                            <InputCurrency
                              placeholder={value.placeholder || ''}
                              type={'text'}
                              {...field}
                              value={field.value as number}
                            />
                            <CurrenciesSelect
                              showSearch={true}
                              placeholder={'USD'}
                              options={sortedList.map((item) => ({
                                key: item.currencyCode,
                                label: item.currencyCode,
                                value: item.currencyCode,
                              }))}
                              defaultValue={selectedCurrency}
                              onChange={(v) => setSelectedCurrency(v)}
                            />
                          </CurrencyWrapper>
                        )}

                        {value.type === QuestionType.TEXT && (
                          <Input
                            placeholder={value.placeholder || ''}
                            type={'text'}
                            {...field}
                            value={field.value as string}
                          />
                        )}

                        {value.type === QuestionType.NUMBER && (
                          <Input
                            placeholder={value.placeholder ?? ''}
                            type={'number'}
                            {...field}
                            value={field.value as number}
                          />
                        )}
                      </Form.Item>
                    </FieldContainerIw>
                  )}
                />
              );
            })}
        </InnerForm>

        <Buttons>
          <ButtonIw htmlType="submit">{t('btn')}</ButtonIw>
        </Buttons>
      </InterwillStyledForm>
    </ModalContainerV1>
  );
};

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
  padding-top: 32px;
  width: 100%;

  ${media.mobile} {
    flex-direction: column;
  }
`;

const ButtonIw = styled(ButtonIwBase)`
  ${media.mobile} {
    width: 100%;
  }
`;

const SectionTitle = styled.span`
  font-weight: 700;
  width: 100%;
`;

const FieldContainerIw = styled(FieldContainerIwBase)<{ $halfWidth?: boolean }>`
  flex: 0 0 100%;
  ${(props) =>
    props.$halfWidth
      ? `
    flex: 0 0 calc(50% - 8px);
    `
      : ''}

  ${media.mobile} {
    flex: 0;
  }
`;

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background: ${COLORS.systemDark100};
  margin: 24px 0;
  flex-shrink: 0;
`;

const InnerForm = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
  max-height: 500px;
  overflow: auto;
  padding: 0 2px;

  ${media.mobile} {
    flex-direction: column;
    max-height: 100%;
    flex-wrap: nowrap;
  }
`;

const CurrencyWrapper = styled.div`
  display: flex;
  border-radius: 8px;
  border: 1px solid ${COLORS.systemDark100};
  padding-right: 5px;
`;

const InputCurrency = styled(Input)`
  && {
    border: none;
    flex-grow: 3;
    flex-shrink: 1;
  }
`;

const CurrenciesSelect = styled(CurrenciesSelectBase)`
  flex-shrink: 4;

  .ant-select-selector {
    border-radius: 0 !important;
    border: none !important;
    border-left: 1px solid ${COLORS.systemDark100} !important;
  }
`;

const AlertStyled = styled(Alert)`
  margin-top: 24px;
`;
